import i18n from 'i18next';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { get, getTodos } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import type { ServicoOpcao } from './ItensContrato';
import { CAMINHO_CONTRATOS, CAMINHO_PLANOS, CAMINHO_REAJUSTES, type Contrato, type LinhaMrr } from './tipos';
import { rotuloForma } from '../../nucleo/forma-pagamento';

/** Recarrega tudo que o contrato mexe: lista, detalhe, reajustes, MRR e o Início. */
export async function invalidarAssinaturas(qc: QueryClient) {
  await Promise.all([
    qc.invalidateQueries({ queryKey: [CAMINHO_CONTRATOS] }),
    qc.invalidateQueries({ queryKey: [CAMINHO_REAJUSTES] }),
    qc.invalidateQueries({ queryKey: [CAMINHO_PLANOS] }),
    qc.invalidateQueries({ queryKey: ['assinaturas', 'opcoes'] }),
    qc.invalidateQueries({ queryKey: ['/assinaturas/mrr'] }),
    qc.invalidateQueries({ queryKey: ['/inicio/indicadores'] }),
  ]);
}

export const useContrato = (id: string | null) =>
  useQuery({ queryKey: [CAMINHO_CONTRATOS, 'detalhe', id], queryFn: () => get<Contrato>(`${CAMINHO_CONTRATOS}/${id}`), enabled: !!id });

export const useMrr = () => useQuery({ queryKey: ['/assinaturas/mrr'], queryFn: () => get<{ referencia: string; data: string; por_moeda: LinhaMrr[] }>('/assinaturas/mrr') });

interface Opcoes {
  servicos: ServicoOpcao[];
  categorias: { valor: string; rotulo: string }[] | null;
  centros: { valor: string; rotulo: string }[] | null;
  formas: { valor: string; rotulo: string }[] | null;
  condicoes: { valor: string; rotulo: string }[] | null;
  /** Pacote 2: planos ativos (para o contrato que nasce do plano). */
  planos: { valor: string; rotulo: string }[];
}

/**
 * Listas de apoio do formulário (getTodos, nunca uma página solta): serviços de venda ativos e formas e condições do
 * Cadastros; categorias de receita e centros de custo do Financeiro só para quem o lê (sem ele, os campos não aparecem
 * e o que já estava gravado não é tocado).
 */
export function useOpcoesContrato() {
  const s = useSessao();
  const leCadastros = s.pode('cadastros.ver');
  const leFinanceiro = s.instalado('financeiro') && s.pode('financeiro.ver');
  return useQuery<Opcoes>({
    queryKey: ['assinaturas', 'opcoes', leCadastros, leFinanceiro, i18n.resolvedLanguage ?? i18n.language],
    staleTime: 30_000,
    queryFn: async () => {
      const lista = <T>(caminho: string, params: Record<string, unknown>) => getTodos<T>(caminho, params).then((r) => r.itens);
      const [servicos, formas, condicoes, categorias, centros, planos] = await Promise.all([
        leCadastros ? lista<ServicoOpcao>('/cadastros/servicos', { uso: 'venda', ativo: true }) : Promise.resolve([]),
        leCadastros ? lista<{ id: string; nome: string; tipo?: string; abreviatura?: string | null }>('/cadastros/formas-pagamento', { ativa: true }) : Promise.resolve(null),
        leCadastros ? lista<{ id: string; nome: string; codigo: string }>('/cadastros/condicoes-pagamento', { ativa: true }) : Promise.resolve(null),
        // Fase 2, W24 (K9): só as analíticas (a sintética só agrupa e o Contas recusa no item da fatura).
        leFinanceiro ? lista<{ id: string; nome: string; codigo: string | null; tipo: string; ativa: boolean }>('/financeiro/categorias', { ativa: true, lancavel: true }) : Promise.resolve(null),
        leFinanceiro ? lista<{ id: string; nome: string; codigo: string | null }>('/financeiro/centros-custo', { ativo: true }) : Promise.resolve(null),
        lista<{ id: string; nome: string }>(CAMINHO_PLANOS, { ativo: true }),
      ]);
      const rotulo = (x: { nome: string; codigo?: string | null }) => [x.codigo, x.nome].filter(Boolean).join(' ');
      return {
        servicos: servicos ?? [],
        formas: formas ? formas.map((f) => ({ valor: f.id, rotulo: rotuloForma(f) })) : null,
        condicoes: condicoes ? condicoes.map((c) => ({ valor: c.id, rotulo: c.nome })) : null,
        categorias: categorias ? categorias.filter((c) => c.tipo === 'receita').map((c) => ({ valor: c.id, rotulo: rotulo(c) })) : null,
        centros: centros ? centros.map((c) => ({ valor: c.id, rotulo: rotulo(c) })) : null,
        planos: planos.map((p) => ({ valor: p.id, rotulo: p.nome })),
      };
    },
  });
}
